import {
  joinOrderDetails,
  journalItemLabel,
  journalStatusCode,
  lastOrderErrorText,
  orderFailureLabelKey,
  type PlacementProgress,
} from "./journalOrderStatus";
import type { QuickCartItem } from "./quickCartGrouping";

// Отказы оформления из Быстрой корзины (CAB-782): как назвать причину, как
// сложить сообщение и как часто спрашивать журнал о судьбе отправленных позиций.
// Чистые функции — стор только оркестрирует.

export type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * Отметка отказа на строке панели: причина словами и где теперь позиция.
 * `inJournal` — заявку приняли, но поставщик заказ не создал: позиция уже в
 * основной корзине («Оформлено · Ошибка»), из панели её только показать и
 * увести в журнал. Иначе бэк отказал сразу, и позиция осталась в корзине.
 */
export interface QuickCartRowFailure {
  reason: string;
  inJournal: boolean;
}

/** Отказ оформления, показанный в Быстрой корзине: позиция и причина словами. */
export interface QuickCartPlacementFailure {
  thash: string;
  item: QuickCartItem;
  /** Код статуса строки без номера попытки (4 — поставщик не принял заказ, 9 — сбой). */
  status: number;
  reason: string;
}

/**
 * Причина отказа словами: текст бэка, иначе подпись статуса, иначе общий текст.
 * Пустая причина в тосте и на строке недопустима — «ошибка» без объяснения и
 * была тем, на что жаловался клиент.
 */
export function orderRejectionReason(status: number, error: string, translate: Translate): string {
  if (error.trim().length > 0) return error.trim();
  const key = orderFailureLabelKey(status);
  return key ? translate(key) : translate("quickCart.orderFailed");
}

/** Причина отказа по строке журнала: последняя попытка, поле error, подпись статуса. */
export function placementFailureReason(row: Record<string, unknown>, translate: Translate): string {
  const status = journalStatusCode(row.status) ?? 0;
  return orderRejectionReason(status, lastOrderErrorText(row) ?? "", translate);
}

// Сколько позиций называем поимённо — как в журнале: длинное перечисление в
// тосте не читают, а на телефоне оно занимает пол-экрана.
const FAILURE_DETAILS_LIMIT = 3;

/** «17177 NARVA — причина» по каждой позиции, лишнее — «и ещё N поз.». */
export function describePlacementFailures(failures: readonly QuickCartPlacementFailure[], translate: Translate) {
  const lines = failures.map((failure) => {
    const label = journalItemLabel(failure.item);
    return label ? `${label} — ${failure.reason}` : failure.reason;
  });
  return joinOrderDetails(lines, FAILURE_DETAILS_LIMIT, (rest) =>
    translate("journal.orderDetailsMore", { count: rest }),
  );
}

/**
 * Текст тоста об отказе (CAB-782): что не оформилось и почему, куда делась
 * позиция и что с ней можно сделать. Для одной и нескольких позиций — разные
 * формулировки: «позиция перенесена» про три строки читается как про одну.
 */
export function placementFailureMessage(failures: readonly QuickCartPlacementFailure[], translate: Translate): string {
  const details = describePlacementFailures(failures, translate);
  const key = failures.length === 1 ? "quickCart.placementFailedOne" : "quickCart.placementFailedMany";
  return translate(key, { details });
}

/** Пока бэк недавно двигал строку, спрашиваем часто; затянувшееся ожидание — редко. */
export const PLACEMENT_POLL_FAST_MS = 2_000;
export const PLACEMENT_POLL_SLOW_MS = 15_000;
/** Сколько ждём быстрых сдвигов статуса, прежде чем перейти на редкий опрос. */
export const PLACEMENT_FAST_WINDOW_MS = 120_000;
/**
 * Строка так и не сдвинулась — забываем о ней. Дольше журнала (2 мин): там за
 * строкой смотрит открытая страница, а здесь — фон, и медленный поставщик не
 * должен оставить отказ без объяснения.
 */
export const PLACEMENT_TIMEOUT_MS = 30 * 60_000;

/** Пауза до следующего опроса по самому «свежему» из ожидающих состояний. */
export function placementPollDelay(pending: readonly PlacementProgress[], now: number): number {
  const recent = pending.some((progress) => now - progress.seenAt < PLACEMENT_FAST_WINDOW_MS);
  return recent ? PLACEMENT_POLL_FAST_MS : PLACEMENT_POLL_SLOW_MS;
}
